// src/components/PipelineBar.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "../styles/PipelineBar.css";

/**
 * Single-select pipeline bar.
 * - Loads statuses + current from API
 * - Does NOT write to the server
 * - Calls onSelect(newId) so parent can save later
 */
export default function PipelineBar({
  userId,
  show,
  canEdit,
  selectedId, // <- parent-controlled selected value (can be undefined)
  onSelect, // <- parent setter (receives pls_id)
}) {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [statuses, setStatuses] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // Effective value to show: pending selection (from parent) OR current from API
  const effectiveId = useMemo(
    () => (selectedId ? Number(selectedId) : Number(currentId)),
    [selectedId, currentId]
  );

  useEffect(() => {
    if (!show || !userId) return;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const [s, u] = await Promise.all([
          axios.get(`${BASE_URL}/api/pipeline/statuses`, { headers }),
          axios.get(`${BASE_URL}/api/pipeline/users/${userId}`, { headers }),
        ]);
        setStatuses(Array.isArray(s.data) ? s.data : []);
        setCurrentId(u.data?.current?.pipeline_status ?? null);
      } catch (e) {
        console.error("Pipeline init failed:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            `Failed to load pipeline (HTTP ${e?.response?.status || "ERR"})`
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [show, userId, BASE_URL]);

  const handleClick = (newId) => {
    if (!canEdit) return;
    if (typeof onSelect === "function") onSelect(newId);
  };

  if (!show) return null;

  return (
    <div className="pipeline-card">
      <div className="pipeline-title">Pipeline</div>
      {loading ? (
        <div className="pipeline-loading">Loading…</div>
      ) : errorMsg ? (
        <div className="pipeline-loading" style={{ color: "#b91c1c" }}>
          {errorMsg}
        </div>
      ) : (
        <div className="pipeline-bar">
          {statuses.map((s) => {
            const active = Number(effectiveId) === Number(s.pls_id);
            return (
              <button
                type="button"
                key={s.pls_id}
                className={["pipeline-step", active ? "active" : ""].join(" ")}
                onClick={() => handleClick(s.pls_id)}
                title={s.pls_name}
                disabled={!canEdit}
                aria-pressed={active}
              >
                <span className="step-label">{s.pls_name}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="pipeline-hint">
        {canEdit
          ? "Click a step to choose it. Changes are saved when you click Save."
          : "Pipeline is view-only."}
      </div>
    </div>
  );
}
